import { Stack } from '@mui/material';
import {
  AssortmentExportColumn,
  assortmentViewExportColumns,
  useExportAssortmentItemsToExcel,
  useFetchAssortmentHierarchyFilterOptions,
  useFetchAssortmentPrices,
} from '@retail/my-assortment/data-access';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import {
  AssortmentPrices,
  AssortmentView,
  HierarchyFilterContext,
  isMainSupplierItem,
} from '@retail/my-assortment/types';
import { FilterChipContainer, ToggleFiltersButton, ToggleFiltersToolbar } from '@retail/components';
import { TotalItemsLoader } from '@retail/products/components';
import { useDisclosure } from '@shared/hooks';
import { AddItemsToAssortment } from '../AddItemsToAssortment';
import { AssortmentPricesItemsContainer } from './AssortmentPricesItemsContainer';
import { AssortmentItemIdentifiersFilterDialog } from '@retail/products/containers';
import { useIncludeVatMyAssortment } from '@retail/my-assortment/context';
import { AssortmentPricesFilters } from '@retail/my-assortment/components';
import dayjs from 'dayjs';
import { AssortmentToolbar } from '../Assortment';
import { Filter, filterList } from '@shared/utils';
import { brandLogo, openAddItemsSearchParam } from '@retail/utils';
import { baseAssortmentFilters } from '@retail/my-assortment/utils';
import { useFilters } from '@retail/products/hooks';
import { createItemIdentifiersPredicate } from '@retail/products/utils';
import { useCallback, useDeferredValue, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export function AssortmentPricesContainer() {
  const { selectedOrgUnitAssortment, appTenant } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();
  const showFilters = useDisclosure(true);
  const itemIdentifiersFilterDialog = useDisclosure(false);
  const { includeVat, toggleIncludeVat } = useIncludeVatMyAssortment();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userCanRead } = usePermissionsForCurrentUser();

  const { filters, setFilters, resetFilters } = useFilters(baseAssortmentFilters);
  const canReadCompeititorPrices =
    userCanRead('MONITORING_ASSORTMENT_ITEMS') && selectedOrgUnitAssortment.isSuperUser;

  const isAddItemsDialogOpen = searchParams.get(openAddItemsSearchParam) === 'true';
  const openAddItems = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    resetFilters([], params);
    params.set(openAddItemsSearchParam, 'true');
    setSearchParams(params, { replace: true });
  }, [resetFilters, searchParams, setSearchParams]);

  const closeAddItems = useCallback(
    (paramsToMutate?: URLSearchParams) => {
      const params = paramsToMutate ?? new URLSearchParams(searchParams);
      params.delete(openAddItemsSearchParam);
      if (!paramsToMutate) {
        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  const { data: filterContextDTO } = useFetchAssortmentHierarchyFilterOptions({
    assortmentId: selectedOrgUnitAssortment.assortmentId,
    supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    suspense: false,
  });

  const filterContext = useMemo(
    () => (filterContextDTO ? HierarchyFilterContext.fromDto(filterContextDTO) : undefined),
    [filterContextDTO]
  );

  const deferredFilters = useDeferredValue(filters);

  const itemIdentifiersPredicate = useMemo(
    () =>
      deferredFilters.itemIdentifiers
        ? createItemIdentifiersPredicate(deferredFilters.itemIdentifiers.identifiers)
        : undefined,
    [deferredFilters.itemIdentifiers]
  );

  const { data: assortmentWithCountDTO, isFetching: isFetchingItems } = useFetchAssortmentPrices({
    assortmentId: filters.forApproval
      ? selectedOrgUnitAssortment.draftAssortmentId
      : selectedOrgUnitAssortment.assortmentId,
    supplierNumber: filters.supplierNumber ? Number(filters.supplierNumber) : undefined,
    productGroupNumbers: filters.productGroupNumbers,
    discountGroupCodes: filters.discountGroupCodes,
    query: filters.searchQuery ?? undefined,
    assortmentTags: filters.subAssortmentIds,
    withAgreementPrices:
      filters.purchasePrice === 'UNSPECIFIED' ? undefined : filters.purchasePrice === 'YES',
    suspense: false,
  });

  const assortmentPrices = useMemo(
    () =>
      assortmentWithCountDTO?.items.map((dto) =>
        AssortmentPrices.fromDTO(dto, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ) ?? [],
    [assortmentWithCountDTO?.items, selectedBrand.brandId, selectedBrand.name]
  );
  const assortmentItemsAfterFrontendFilters = useMemo(() => {
    const frontendFilters: Filter<AssortmentPrices>[] = [
      {
        active: deferredFilters.forApproval,
        predicate: (item) => item.inDraft,
      },
      {
        active: deferredFilters.activeItems !== 'UNSPECIFIED',
        predicate: (item) => {
          const isItemActive = item.isActiveBasedOnDiscontinuationStatus();
          return deferredFilters.activeItems === 'YES' ? isItemActive : !isItemActive;
        },
      },
      {
        active: deferredFilters.inStock !== 'UNSPECIFIED',
        predicate: (item) =>
          deferredFilters.inStock === 'NO'
            ? !item.isStocked
            : item.isStocked || item.isStocked === undefined,
      },
      {
        active: !!itemIdentifiersPredicate,
        predicate: (item) => !!itemIdentifiersPredicate?.(item),
      },
      {
        active: appTenant.hasMainSupplier && deferredFilters.fromMainSupplier,
        predicate: isMainSupplierItem,
      },
    ];
    return filterList({
      filters: frontendFilters,
      initList: assortmentPrices,
    });
  }, [
    assortmentPrices,
    deferredFilters.activeItems,
    deferredFilters.forApproval,
    deferredFilters.inStock,
    deferredFilters.fromMainSupplier,
    appTenant.countryCode,
    itemIdentifiersPredicate,
  ]);

  const { isLoading, exportToExcel } = useExportAssortmentItemsToExcel({
    fileName: `${selectedOrgUnitAssortment.name} assortment ${dayjs().format(
      'YYYY-MM-DD HH:mm:ss'
    )}`,
    items: assortmentItemsAfterFrontendFilters,
    columns: Object.entries(assortmentViewExportColumns)
      .filter(([_, { orgUnits }]) => orgUnits.includes(selectedOrgUnitAssortment.orgType.baseType))
      .map(([col]) => col as AssortmentExportColumn),
  });

  return (
    <Stack flexGrow={1} overflow="hidden">
      <AssortmentToolbar
        exportToExcel={exportToExcel}
        isLoadingExport={isLoading}
        totalItems={assortmentWithCountDTO?.totalNumberOfItems ?? 0}
        onToggleAddItemsDialog={openAddItems}
      />
      <Stack flexGrow={1} overflow="hidden">
        <ToggleFiltersToolbar sx={{ justifyContent: 'space-between', mb: 1 }}>
          <FilterChipContainer
            allowedKeys={baseAssortmentFilters.filter(
              (f) =>
                f !== 'assortmentView' && (appTenant.hasMainSupplier || f !== 'fromMainSupplier')
            )}
            filterOptions={{ ...filterContext, subAssortments: filterContext?.assortmentTags }}
          />
          <Stack flexDirection="row" alignItems="flex-end" gap={1}>
            <TotalItemsLoader
              totalItems={assortmentWithCountDTO?.totalNumberOfItems ?? 0}
              itemsShowing={assortmentItemsAfterFrontendFilters.length}
              isFetching={isFetchingItems}
            />

            <ToggleFiltersButton
              showFilters={showFilters.isOpen}
              toggleFilters={showFilters.onToggle}
            />
          </Stack>
        </ToggleFiltersToolbar>
        <Stack flexGrow={1} flexDirection="row" overflow="hidden">
          <AssortmentPricesItemsContainer
            selectedAssortmentView={filters.assortmentView as AssortmentView}
            items={assortmentItemsAfterFrontendFilters}
            loadingFetchItems={isFetchingItems}
            emptyAssortment={
              !!assortmentWithCountDTO && assortmentWithCountDTO.totalNumberOfItems === 0
            }
            toggleAddItemsDialog={openAddItems}
          />
          <AssortmentPricesFilters
            filterOptions={filterContext}
            includeVat={includeVat}
            showFilters={showFilters.isOpen}
            canReadCompetitorPrices={canReadCompeititorPrices}
            toggleIncludeVat={toggleIncludeVat}
            onOpenItemIdentifiersFilter={itemIdentifiersFilterDialog.onOpen}
            showFromMainSupplier={appTenant.hasMainSupplier}
          />
        </Stack>
      </Stack>

      {itemIdentifiersFilterDialog.isOpen && (
        <AssortmentItemIdentifiersFilterDialog
          assortmentId={selectedOrgUnitAssortment.assortmentId}
          useFetchItems={useFetchAssortmentPrices}
          onApply={(itemIdentifiers) => {
            setFilters({ itemIdentifiers });
            itemIdentifiersFilterDialog.onClose();
          }}
          onClose={itemIdentifiersFilterDialog.onClose}
        />
      )}

      {isAddItemsDialogOpen && (
        <AddItemsToAssortment
          open
          onClose={closeAddItems}
          assortmentId={selectedOrgUnitAssortment.assortmentId}
        />
      )}
    </Stack>
  );
}
